import { Field } from 'formik';
import { clsx } from 'clsx';
import { ChangeEvent } from 'react';
import { FormikSwitchWrapper } from '../../../switch/FormikSwitchWrapper';
import { FormControl } from '../../../shared/components/FormControl/FormControl';
import { FormikTextField } from '../../../input/FormikTextField/FormikTextField';
import { TemplateFormFieldProps } from '../../interfaces/template-form';
import { CopyToClipboardButton } from '../../../button/CopyToClipboardButton/CopyToClipboardButton';
import styles from './TemplateFormSSHConfigField.module.scss';
import { TemplateFormSshConfigFieldMeta } from './interfaces/template-form-ssh-config-field-meta';

export function TemplateFormSSHConfigField({
  field,
  form,
  metadata,
  formDisabled
}: TemplateFormFieldProps) {
  const { sshAuthKeys, disabled } = metadata as TemplateFormSshConfigFieldMeta;

  const renderConfigFields = !!field.value;
  const renderAuthKeys = sshAuthKeys && sshAuthKeys.length > 0;

  const onSSHToggle = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    (async () => {
      await form.setFieldValue(field.name, checked);
      await form.setFieldValue('ssh_host', null);
      await form.setFieldValue('ssh_port', null);
      await form.setFieldValue('ssh_user', null);
    })();
  };

  const disableSSH = disabled || formDisabled;

  return (
    <div>
      <FormikSwitchWrapper
        dataId='ssh'
        fieldName={field.name}
        label='Use SSH'
        disabled={disableSSH}
        switchProps={{ disabled: disableSSH, onChange: onSSHToggle }}
      >
        Connect securely through an SSH tunnel server
      </FormikSwitchWrapper>

      {renderConfigFields ? (
        <div className={clsx(styles.sshConfigFields, 'mt-5')}>
          <FormControl>
            <Field
              name='ssh_host'
              label='SSH IP'
              required
              component={FormikTextField}
              disabled={disableSSH}
              dataId='ssh-host'
            />
          </FormControl>

          <FormControl>
            <Field
              name='ssh_port'
              label='SSH Port'
              required
              component={FormikTextField}
              type='number'
              disabled={disableSSH}
              dataId='ssh-port'
            />
          </FormControl>

          <FormControl>
            <Field
              name='ssh_user'
              label='SSH User'
              required
              component={FormikTextField}
              disabled={disableSSH}
              dataId='ssh-user'
            />
          </FormControl>

          {renderAuthKeys ? (
            <div
              className={clsx(styles.authoriseKey, 'grid-column-span-2')}
              data-id='ssh-auth-keys'
            >
              <p>
                To authenticate via a public key, add our public key to the{' '}
                <span className='text-deferred'>.ssh/authorized_keys</span> file on your SSH server:
              </p>

              {sshAuthKeys.map(authKey => (
                <div className={styles.keyWrapper} key={authKey.key}>
                  <span className='text-medium'>
                    {authKey.displayName}
                    {authKey.recommended ? ' (Recommended)' : ''}
                  </span>

                  <div className={styles.key}>
                    <span className='mr-1'>{authKey.value}</span>

                    <div>
                      <CopyToClipboardButton
                        copyText={authKey.value}
                        dataId={authKey.key}
                        toastMessage='Copied the key to clipboard'
                        size='md'
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
